import { ExtraInfoInput } from "../types/__generated__/globalTypes";
import { safeJsonParse } from "./tools";

/**
 * The complete set of fields `ExtraInfoInput` accepts.
 *
 * `ShoppingCartItem.extraInfo` is asymmetric: the server returns it as an opaque
 * JSON scalar but only accepts it back as this closed input object. The backend
 * writes its own internal keys into that blob — e.g.
 * `isSelectByStyleRecommendation`, which marks a wine the select-by-style
 * recommender chose rather than the customer — and those keys are deliberately
 * not part of the input type. Echoing one back fails GraphQL validation before
 * the request reaches any resolver, taking the whole mutation with it.
 *
 * Declared as `Record<keyof ExtraInfoInput, true>` so that `yarn generate-types`
 * adding a field to `ExtraInfoInput` fails `yarn tsc` here until this list is
 * updated — the schema can't drift away from the allowlist silently.
 */
const EXTRA_INFO_INPUT_FIELDS: Record<keyof ExtraInfoInput, true> = {
  toMemberEmail: true,
  toMemberFirstName: true,
  toMemberLastName: true,
  toMemberMobileCountryCode: true,
  toMemberMobileNumber: true,
  toMemberPersonalNote: true,
  toMemberShippingAddressId: true,
  holdUntilDate: true,
  giftDeliveryId: true,
  giftTemplateId: true,
  redBottles: true,
  whiteBottles: true,
  sparklingBottles: true,
  roseBottles: true,
  monthsPurchased: true,
  bottlesPerBox: true,
  spendPerBox: true,
  personalNote: true,
  shippingAddressId: true,
  isGift: true,
  pairingRequestId: true,
};

const ALLOWED_FIELDS = Object.keys(EXTRA_INFO_INPUT_FIELDS) as (keyof ExtraInfoInput)[];

/**
 * Narrows a cart item's `extraInfo` blob down to the fields `ExtraInfoInput`
 * accepts, so it can be safely sent back on an add/update cart mutation.
 *
 * Anything the server added that isn't an input field is dropped — including
 * `__typename` on blobs read out of the Apollo cache. Keys the backend owns
 * (currently only `isSelectByStyleRecommendation`) are re-applied server-side
 * by `ShoppingCart.update_shopping_cart_item`, so dropping them here loses
 * nothing.
 *
 * `false` and `0` are preserved — only absent and `undefined` values are
 * skipped — because `isGift: false` is how a gift is un-ticked.
 *
 * @param value serialized JSON string, an already-parsed object, or nullish
 * @returns the filtered input, or `undefined` when there's nothing to send
 * */
export const sanitiseExtraInfoInput = (value: unknown): ExtraInfoInput | undefined => {
  const parsed = safeJsonParse(value, null);
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
    return undefined;
  }

  const sanitised: Partial<Record<keyof ExtraInfoInput, unknown>> = {};
  ALLOWED_FIELDS.forEach((field) => {
    const fieldValue = (parsed as Record<string, unknown>)[field];
    if (fieldValue !== undefined) {
      sanitised[field] = fieldValue;
    }
  });

  return Object.keys(sanitised).length ? (sanitised as ExtraInfoInput) : undefined;
};
